/**
 * AuthContext
 *
 * Contexto global de autenticación. Reemplaza el patrón de hook-por-componente
 * (useAuth) que creaba N subscripciones a onAuthStateChange, una por cada
 * componente montado, causando 50+ re-renders en cascada por cada navegación.
 *
 * Ahora hay UNA sola subscripción en AuthProvider y todos los componentes
 * leen el mismo estado via useContext.
 *
 * IMPORTANTE: Las funciones internas usan refs para acceder al estado más
 * reciente sin necesidad de re-subscribirse cuando el estado cambia.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase';
import type { Session, User } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import { EDGE_FUNCTIONS } from '../config/functions.config';
import { logError, logWarn } from '../lib/logger';

// ─── Tenant profile cache (module-level, shared across all instances) ─────────

type UserRole = 'owner' | 'manager' | 'employee' | 'cashier' | null;

type TenantProfileResult = {
  tenantId: string;
  branchId: string | null;
  role: UserRole;
  storeSlug: string | null;
} | null;

/**
 * Resumen de un tenant al que el user tiene acceso. Usado por el TenantSwitcher.
 * Multi-tenant per user — Roadmap §18.
 */
export interface TenantSummary {
  id: string;
  name: string;
  slug: string | null;
  role: UserRole;
  /** Si es la línea/branch principal del user para este tenant. */
  branchId: string | null;
}

/**
 * Routing mode V1/V2/V3 — Roadmap §18.
 * V1 (HOY): 'state' — switching solo en localStorage + memoria.
 * V2 (>1k users): 'url-path' — `/t/{slug}/...`
 * V3 (>10k users): 'subdomain' — `{slug}.smarty.lat`
 */
export type RoutingMode = 'state' | 'url-path' | 'subdomain';
export const ROUTING_MODE: RoutingMode = 'state';

const TENANT_PROFILE_CACHE_TTL_MS = 30_000;
const tenantProfileCache = new Map<string, { result: TenantProfileResult; expiresAt: number }>();
const tenantProfileInFlight = new Map<string, Promise<TenantProfileResult>>();

// Cache de la lista completa de tenants del user (multi-tenant V1).
const allTenantsCache = new Map<string, { result: TenantSummary[]; expiresAt: number }>();
const allTenantsInFlight = new Map<string, Promise<TenantSummary[]>>();

// ─── Raw DB fetchers ──────────────────────────────────────────────────────────

async function fetchTenantFromProfileRaw(
  supabaseClient: SupabaseClient,
  userId: string,
  preferredTenantId?: string | null,
): Promise<TenantProfileResult> {
  // Multi-tenant V1 (§18): si el user hizo switch a un tenant específico,
  // verificar acceso a ESE tenant antes de caer al RPC genérico (que retorna
  // el primer tenant del user, típicamente el más viejo).
  if (preferredTenantId) {
    const preferred = await fetchTenantFallback(supabaseClient, userId, preferredTenantId);
    if (preferred && preferred.tenantId === preferredTenantId) return preferred;
  }

  const timeoutMs = 3000;
  const timeout = (msg: string) =>
    new Promise<never>((_, rej) => setTimeout(() => rej(new Error(msg)), timeoutMs));
  try {
    const rpcPromise = supabaseClient.rpc('get_tenant_profile_for_user', { p_user_id: userId });
    const result = (await Promise.race([rpcPromise, timeout('rpc')])) as {
      data: Array<{ tenant_id: string; branch_id: string | null; role: string; slug: string | null }> | null;
      error: unknown;
    };
    if (result.error || !result.data?.length) return null;
    const row = result.data[0];
    if (!row?.tenant_id) return null;
    return {
      tenantId: row.tenant_id,
      branchId: row.branch_id ?? null,
      role: (row.role as 'owner' | 'manager' | 'employee' | 'cashier') ?? null,
      storeSlug: row.slug ?? null,
    };
  } catch {
    return fetchTenantFallback(supabaseClient, userId);
  }
}

async function fetchTenantFallback(
  supabaseClient: SupabaseClient,
  userId: string,
  preferredTenantId?: string | null,
): Promise<TenantProfileResult> {
  // Si hay un preferred (lastActiveTenantId), verificar acceso primero.
  // Esto cubre el caso multi-tenant: sin esta rama, el fallback retornaba
  // el primer tenant que encontrara, ignorando el switch del user.
  if (preferredTenantId) {
    try {
      const { data: ut } = await supabaseClient
        .from('user_tenants')
        .select('tenant_id, role')
        .eq('user_id', userId)
        .eq('tenant_id', preferredTenantId)
        .maybeSingle();

      if (ut?.tenant_id) {
        const [{ data: t }, { data: spRow }] = await Promise.all([
          supabaseClient.from('tenants').select('slug').eq('id', preferredTenantId).maybeSingle(),
          supabaseClient
            .from('staff_profiles')
            .select('branch_id, role')
            .eq('user_id', userId)
            .eq('tenant_id', preferredTenantId)
            .maybeSingle(),
        ]);
        return {
          tenantId: preferredTenantId,
          branchId: spRow?.branch_id ?? null,
          role: ((spRow?.role ?? ut.role) as 'owner' | 'manager' | 'employee' | 'cashier') ?? null,
          storeSlug: (t as { slug?: string | null } | null)?.slug ?? null,
        };
      }
    } catch {
      // Fallthrough al fallback original
    }
  }

  const timeoutMs = 3000;
  const ut = supabaseClient.from('user_tenants').select('tenant_id, role').eq('user_id', userId).maybeSingle();
  const sp = supabaseClient.from('staff_profiles').select('tenant_id, branch_id, role').eq('user_id', userId).maybeSingle();
  const to = (msg: string) => new Promise<never>((_, rej) => setTimeout(() => rej(new Error(msg)), timeoutMs));
  const [utRes, spRes] = (await Promise.all([
    Promise.race([ut, to('ut')]),
    Promise.race([sp, to('sp')]),
  ])) as Array<{ data: { tenant_id?: string; branch_id?: string | null; role?: string } | null; error: unknown }>;

  const sp_ = spRes.data;
  if (!spRes.error && sp_?.tenant_id) {
    const { data: t } = await supabaseClient.from('tenants').select('slug').eq('id', sp_.tenant_id).maybeSingle();
    return {
      tenantId: sp_.tenant_id,
      branchId: sp_.branch_id ?? null,
      role: (sp_.role as 'owner' | 'manager' | 'employee' | 'cashier') ?? null,
      storeSlug: t?.slug ?? null,
    };
  }

  const ut_ = utRes.data;
  if (!utRes.error && ut_?.tenant_id) {
    let branchId: string | null = null;
    if (sp_?.tenant_id === ut_.tenant_id && sp_?.branch_id) branchId = sp_.branch_id;
    if (!branchId) {
      const { data: b } = await supabaseClient
        .from('branches').select('id').eq('tenant_id', ut_.tenant_id).eq('is_active', true).limit(1).single();
      if (b) branchId = b.id;
    }
    const { data: t } = await supabaseClient.from('tenants').select('slug').eq('id', ut_.tenant_id).maybeSingle();
    return {
      tenantId: ut_.tenant_id,
      branchId,
      role: (ut_.role as 'owner' | 'manager' | 'employee' | 'cashier') ?? null,
      storeSlug: t?.slug ?? null,
    };
  }

  return null;
}

/**
 * Carga TODOS los tenants a los que el user pertenece (multi-tenant V1 — §18).
 *
 * Estrategia: 1 query a user_tenants con JOIN a tenants para nombres/slugs.
 * Performance: O(N) donde N = tenants del user (típicamente 1-5, max 50).
 *
 * - Si el user tiene 1 solo tenant → backwards-compat con código legacy
 * - Si tiene N tenants → la UI puede mostrar el switcher
 *
 * NO usa el RPC `get_tenant_profile_for_user` porque ese fue diseñado para 1 tenant.
 * Para V2 (>1k users) considerar crear `get_all_tenants_for_user` RPC con caching.
 */
async function fetchAllTenantsForUser(
  supabaseClient: SupabaseClient,
  userId: string,
): Promise<TenantSummary[]> {
  // user_tenants es la fuente de verdad para multi-tenant. staff_profiles
  // tiene el branch pero ya viene del query de tenant individual.
  const { data, error } = await supabaseClient
    .from('user_tenants')
    .select('tenant_id, role, tenants:tenant_id (id, name, slug)')
    .eq('user_id', userId);

  if (error || !data) {
    logError('[AuthContext] Error cargando tenants del user', error);
    return [];
  }

  type Row = {
    tenant_id: string;
    role: string | null;
    tenants: { id: string; name: string; slug: string | null } | null;
  };
  return (data as unknown as Row[])
    .filter((r) => r.tenants !== null)
    .map((r) => ({
      id: r.tenant_id,
      name: r.tenants!.name,
      slug: r.tenants!.slug,
      role: (r.role as UserRole) ?? null,
      branchId: null,
    }));
}

/** Cached + in-flight dedup de fetchAllTenantsForUser. */
async function fetchAllTenantsCached(
  supabaseClient: SupabaseClient,
  userId: string,
): Promise<TenantSummary[]> {
  const cached = allTenantsCache.get(userId);
  if (cached && cached.expiresAt > Date.now()) return cached.result;

  let promise = allTenantsInFlight.get(userId);
  if (promise) return promise;

  promise = fetchAllTenantsForUser(supabaseClient, userId).then((result) => {
    allTenantsCache.set(userId, { result, expiresAt: Date.now() + TENANT_PROFILE_CACHE_TTL_MS });
    allTenantsInFlight.delete(userId);
    return result;
  });
  allTenantsInFlight.set(userId, promise);
  return promise;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function getCachedTenant(userId: string): {
  tenantId: string;
  branchId: string | null;
  role: 'owner' | 'manager' | 'employee' | 'cashier' | null;
  storeSlug: string | null;
} | null {
  try {
    const raw = localStorage.getItem(`tenant_${userId}`);
    if (raw) {
      const p = JSON.parse(raw);
      if (p.timestamp && Date.now() - p.timestamp < 3_600_000 && p.tenantId) {
        return { tenantId: p.tenantId, branchId: p.branchId || null, role: p.role || null, storeSlug: p.storeSlug ?? null };
      }
    }
  } catch { /* ignore */ }
  return null;
}

function setCachedTenant(
  userId: string,
  tenantId: string,
  branchId: string | null,
  role?: 'owner' | 'manager' | 'employee' | 'cashier' | null,
  storeSlug?: string | null,
) {
  try {
    localStorage.setItem(`tenant_${userId}`, JSON.stringify({ tenantId, branchId, role, storeSlug: storeSlug ?? null, timestamp: Date.now() }));
    localStorage.setItem('cached_tenant_id', tenantId);
    if (branchId) localStorage.setItem('cached_branch_id', branchId);
  } catch { /* ignore */ }
}

function readInitialCache(): {
  tenantId: string | null;
  branchId: string | null;
  role: 'owner' | 'manager' | 'employee' | 'cashier' | null;
  storeSlug: string | null;
} {
  try {
    return {
      tenantId: localStorage.getItem('cached_tenant_id'),
      branchId: localStorage.getItem('cached_branch_id'),
      role: null,
      storeSlug: null,
    };
  } catch {
    return { tenantId: null, branchId: null, role: null, storeSlug: null };
  }
}

// ─── Context types ────────────────────────────────────────────────────────────

export interface AuthContextValue {
  session: Session | null;
  /** Tenant ACTIVO. Alias de `activeTenantId` por backwards-compat. */
  tenantId: string | null;
  storeSlug: string | null;
  branchId: string | null;
  userRole: UserRole;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isOwnerOrManager: () => boolean;

  // ─── Multi-tenant per user (Roadmap §18) ────────────────────────────────
  /** Lista completa de tenants del user. Vacío si el user no tiene ninguno. */
  tenants: TenantSummary[];
  /** ID del tenant actualmente activo. Igual a `tenantId` (alias semántico). */
  activeTenantId: string | null;
  /** Resumen del tenant activo (find en `tenants`), o null si no hay activo. */
  activeTenant: TenantSummary | null;
  /** true si el user tiene >1 tenant (UI muestra switcher). */
  hasMultipleTenants: boolean;
  /**
   * Cambia el tenant activo. SPA switch V1: invalida queryClient + realtime,
   * actualiza localStorage, dispara re-render.
   * @returns Promise que resuelve cuando el switch completó (queries invalidadas).
   */
  switchTenant: (tenantId: string) => Promise<void>;
  /**
   * Refresca la lista `tenants` del user invalidando el cache module-level.
   * Útil después de crear/eliminar tenant: el cache no se entera solo.
   */
  refreshTenants: () => Promise<void>;
  /** Routing mode actual (V1: 'state'). Punto de extensión para V2/V3. */
  routingMode: RoutingMode;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const supabase = getSupabaseClient();
  const initialCache = useMemo(() => readInitialCache(), []);

  const [session, setSession] = useState<Session | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(initialCache.tenantId);
  const [storeSlug, setStoreSlug] = useState<string | null>(initialCache.storeSlug);
  const [branchId, setBranchId] = useState<string | null>(initialCache.branchId);
  const [userRole, setUserRole] = useState<UserRole>(initialCache.role);
  const [loading, setLoading] = useState(true);

  // ─── Multi-tenant V1 (§18) — lista completa de tenants del user ─────────
  const [tenants, setTenants] = useState<TenantSummary[]>([]);

  // Refs so async callbacks always have latest values without triggering re-subscriptions
  const mountedRef = useRef(true);
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  // ─── Tenant resolution ───────────────────────────────────────────────────

  const resolveTenant = useCallback(
    async (userId: string, preferredTenantId?: string | null): Promise<TenantProfileResult> => {
      const apply = (r: TenantProfileResult) => {
        if (r && mountedRef.current) {
          setTenantId(r.tenantId);
          setStoreSlug(r.storeSlug);
          setBranchId(r.branchId);
          setUserRole(r.role);
        }
      };

      // Si pasaron un preferred y coincide con el cache → usar cache.
      // Si difiere → invalidar (el user hizo switch o multi-tenant V1).
      const cached = tenantProfileCache.get(userId);
      if (cached && cached.expiresAt > Date.now()) {
        if (!preferredTenantId || cached.result?.tenantId === preferredTenantId) {
          apply(cached.result);
          return cached.result;
        }
        tenantProfileCache.delete(userId);
        tenantProfileInFlight.delete(userId);
      }

      let promise = tenantProfileInFlight.get(userId);
      if (promise) {

        const result = await promise;
        apply(result);
        return result;
      }

      promise = fetchTenantFromProfileRaw(supabase, userId, preferredTenantId).then((result) => {
        tenantProfileCache.set(userId, { result, expiresAt: Date.now() + TENANT_PROFILE_CACHE_TTL_MS });
        tenantProfileInFlight.delete(userId);
        return result;
      });
      tenantProfileInFlight.set(userId, promise);

      try {
        const result = await promise;
        if (!result) logWarn('[AuthContext] No se encontró tenant');
        apply(result);
        return result;
      } catch (error) {
        tenantProfileInFlight.delete(userId);
        logError('[AuthContext] Error al buscar tenant', error);
        throw error;
      }
    },
    [supabase],
  );

  // ─── Super Admin helpers ─────────────────────────────────────────────────
  // These functions intentionally do NOT appear in the subscription useEffect's
  // dependency array — they are called from inside processSession which itself
  // is stored in a ref, so React never recreates the subscription.

  async function validateAdminToken(token: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.functions.invoke(EDGE_FUNCTIONS.VALIDATE_ADMIN_LOGIN_TOKEN, { body: { token } });

      if (error || !data?.success) {
        logError('[AuthContext] Token inválido', error || data?.error);
        const p = new URLSearchParams(window.location.search);
        p.delete('admin_token');
        window.history.replaceState({}, '', window.location.pathname);
        return null;
      }

      if (data.magic_link) {

        window.location.href = data.magic_link;
        return data.tenant_id;
      }

      if (data.tenant_id) {
        const cur = sessionRef.current ?? (await supabase.auth.getSession()).data.session;
        if (cur?.user) {
          if (!sessionRef.current && mountedRef.current) setSession(cur);
          setCachedTenant(cur.user.id, data.tenant_id, null, 'owner', null);
          localStorage.setItem('cached_tenant_id', data.tenant_id);
          if (mountedRef.current) {
            setTenantId(data.tenant_id);
            setStoreSlug(null);
            setBranchId(null);
            setUserRole('owner');
            setLoading(false);
          }
          const p = new URLSearchParams(window.location.search);
          p.delete('admin_token');
          window.history.replaceState({}, '', window.location.pathname);
          return data.tenant_id;
        }
      }
      return null;
    } catch (err) {
      logError('[AuthContext] Error validando admin token', err);
      return null;
    }
  }

  async function checkSuperAdminLogin(currentSession: Session | null): Promise<string | null> {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const adminToken = urlParams.get('admin_token');
      if (adminToken) return validateAdminToken(adminToken);

      const superAdminTenantId = urlParams.get('super_admin_tenant');
      if (!superAdminTenantId) return null;

      let finalSession = currentSession;
      if (!finalSession?.user) {
        for (let i = 0; i < 10 && !finalSession?.user; i++) {
          await new Promise((r) => setTimeout(r, 500));
          const { data: { session: s }, error } = await supabase.auth.getSession();
          if (error) { logError('[AuthContext] Error obteniendo sesión', error); break; }
          if (s?.user) { finalSession = s; if (mountedRef.current) setSession(s); break; }
        }
        if (!finalSession?.user) {
          logWarn('[AuthContext] No hay sesión después de esperar.');
          urlParams.delete('super_admin_tenant');
          window.history.replaceState({}, '', window.location.pathname);
          return null;
        }
      }

      // Lo decide la base (`platform_admins` vía `is_super_admin()`), no una
      // lista de emails traída en
      // el bundle: era la cuarta copia de la misma lista y la que autorizaba
      // honrar `?super_admin_tenant=`. La consulta a `tenants` de abajo ya
      // aplica RLS, así que esto es la primera de dos barreras, no la única.
      const { data: esAdmin, error: adminError } = await supabase.rpc('is_super_admin');
      if (adminError || esAdmin !== true) {
        logWarn('[AuthContext] Usuario no es Super Admin', { email: finalSession.user.email });
        urlParams.delete('super_admin_tenant');
        window.history.replaceState({}, '', window.location.pathname);
        return null;
      }

      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants').select('id').eq('id', superAdminTenantId).single();
      if (tenantError || !tenantData) {
        logError('[AuthContext] Tenant no encontrado', tenantError);
        urlParams.delete('super_admin_tenant');
        window.history.replaceState({}, '', window.location.pathname);
        return null;
      }

      setCachedTenant(finalSession.user.id, superAdminTenantId, null, 'owner', null);
      localStorage.setItem('cached_tenant_id', superAdminTenantId);
      if (mountedRef.current) {
        setTenantId(superAdminTenantId);
        setStoreSlug(null);
        setBranchId(null);
        setUserRole('owner');
        setLoading(false);
      }
      urlParams.delete('super_admin_tenant');
      window.history.replaceState({}, '', window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : ''));
      return superAdminTenantId;
    } catch (err) {
      logError('[AuthContext] Error en Super Admin login', err);
      return null;
    }
  }

  // ─── Session processor ────────────────────────────────────────────────────

  async function processSession(newSession: Session | null) {
    if (!mountedRef.current) return;
    setSession(newSession);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('super_admin_tenant') || urlParams.has('admin_token')) {
      const result = await checkSuperAdminLogin(newSession);
      if (result) return;
    }

    if (newSession?.user) {
      const uid = newSession.user.id;

      // Multi-tenant V1 (§18): cargar lista de tenants SIEMPRE en background,
      // independiente del path de resolución del activo. Antes vivía solo en
      // el cold path (Path 3) → en Path 1/2 nunca se cargaba la lista, y el
      // switcher quedaba vacío después de un TOKEN_REFRESHED.
      fetchAllTenantsCached(supabase, uid)
        .then((list) => {
          if (mountedRef.current) setTenants(list);
        })
        .catch((err) => {
          logError('[AuthContext] Error cargando lista de tenants', err);
        });

      // Multi-tenant V1 (§18): el user pudo haber hecho switch a un tenant
      // distinto al que está en JWT metadata. lastActiveTenantId refleja la
      // última intención del user — debe ganar sobre meta.tenant_id (legacy).
      const lastActive = (() => {
        try { return localStorage.getItem('lastActiveTenantId'); } catch { return null; }
      })();

      // 1. Metadata + lastActive (instantáneo)
      const meta = newSession.user.user_metadata;
      const metaTid = (meta?.tenant_id as string | undefined) ?? null;
      const tidFromFastPath = lastActive ?? metaTid;
      if (tidFromFastPath) {
        const tid = tidFromFastPath;
        // branch_id solo aplica si el tenant coincide con el del JWT meta
        const bid = (tid === metaTid ? (meta?.branch_id as string | null) ?? null : null);
        // Buscar storeSlug + role en cache local (tenant_${uid}) para evitar
        // que StoreGuard redirija a /login mientras resolveTenant carga en BG.
        const localCache = getCachedTenant(uid);
        const cachedMatches = localCache?.tenantId === tid;
        if (mountedRef.current) {
          setTenantId(tid);
          setBranchId(bid);
          if (cachedMatches) {
            setStoreSlug(localCache.storeSlug);
            setUserRole(localCache.role);
            setLoading(false);
          }
          // Si el cache local NO coincide con tid (post-switch a tenant nuevo
          // sin haber refrescado el cache local), dejamos loading=true hasta
          // que resolveTenant termine — StoreGuard espera mientras loading.
        }
        localStorage.setItem('cached_tenant_id', tid);
        if (bid) localStorage.setItem('cached_branch_id', bid);
        resolveTenant(uid, tid).then((r) => {
          if (!mountedRef.current) return;
          if (r) {
            // Solo aplicar si resolveTenant respetó el preferred (multi-tenant safe)
            const respectedPreferred = r.tenantId === tid;
            if (respectedPreferred) {
              setUserRole(r.role);
              setStoreSlug(r.storeSlug);
              setCachedTenant(uid, tid, bid, r.role, r.storeSlug);
              if (r.branchId && r.branchId !== bid) { setBranchId(r.branchId); localStorage.setItem('cached_branch_id', r.branchId); }
            } else {
              // Preferred no es accesible — usar lo que retorna DB
              setTenantId(r.tenantId); setStoreSlug(r.storeSlug); setBranchId(r.branchId); setUserRole(r.role);
              setCachedTenant(uid, r.tenantId, r.branchId, r.role, r.storeSlug);
              localStorage.setItem('cached_tenant_id', r.tenantId);
              localStorage.setItem('lastActiveTenantId', r.tenantId);
            }
          }
          // Garantizar loading=false aún si el cache local no coincidía con tid
          // (ej. tenant nuevo recién creado: cache local apuntaba al viejo).
          setLoading(false);
        }).catch(() => {
          if (mountedRef.current) setLoading(false);
        });
        return;
      }

      // 2. localStorage cache (instantáneo)
      const cached = getCachedTenant(uid);
      if (cached) {
        if (mountedRef.current) {
          setTenantId(cached.tenantId);
          setStoreSlug(cached.storeSlug);
          setBranchId(cached.branchId);
          setUserRole(cached.role);
          setLoading(false);
        }
        localStorage.setItem('cached_tenant_id', cached.tenantId);
        if (cached.branchId) localStorage.setItem('cached_branch_id', cached.branchId);
        // Background refresh — preferir el tenant cached para no pisarlo
        resolveTenant(uid, cached.tenantId).then((r) => {
          if (r && mountedRef.current) {
            setCachedTenant(uid, r.tenantId, r.branchId, r.role, r.storeSlug);
            if (r.tenantId !== cached.tenantId || r.branchId !== cached.branchId) {
              setTenantId(r.tenantId); setBranchId(r.branchId); setStoreSlug(r.storeSlug);
              localStorage.setItem('cached_tenant_id', r.tenantId);
              if (r.branchId) localStorage.setItem('cached_branch_id', r.branchId);
            }
            if (r.role && r.role !== cached.role) setUserRole(r.role);
            if (r.storeSlug && r.storeSlug !== cached.storeSlug) setStoreSlug(r.storeSlug);
          }
        }).catch(() => {});
        return;
      }

      // 3. Cold path: DB fetch (sin preferred porque no hay info previa)
      resolveTenant(uid, lastActive)
        .then((r) => {
          if (!mountedRef.current) return;
          if (r) {
            setCachedTenant(uid, r.tenantId, r.branchId, r.role, r.storeSlug);
            setTenantId(r.tenantId); setStoreSlug(r.storeSlug); setBranchId(r.branchId); setUserRole(r.role);
            localStorage.setItem('cached_tenant_id', r.tenantId);
            if (r.branchId) localStorage.setItem('cached_branch_id', r.branchId);
          }
          setLoading(false);
        })
        .catch((err) => {
          logError('[AuthContext] Error al cargar tenant', err);
          if (mountedRef.current) setLoading(false);
        });
    } else {
      logWarn('[AuthContext] Sin sesión');
      if (mountedRef.current) {
        setTenantId(null); setStoreSlug(null); setBranchId(null); setUserRole(null);
        setTenants([]);
        setLoading(false);
      }
      try { localStorage.removeItem('cached_tenant_id'); localStorage.removeItem('cached_branch_id'); } catch { /* ignore */ }
    }
  }

  // Store processSession in a ref so the subscription callback always calls
  // the latest version without needing to recreate the subscription
  const processSessionRef = useRef(processSession);
  processSessionRef.current = processSession;

  // ─── Single subscription — runs ONCE only ────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;

    // Check for admin params at mount before waiting for onAuthStateChange
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('admin_token') || urlParams.has('super_admin_tenant')) {
      const token = urlParams.get('admin_token');
      if (token) {
        validateAdminToken(token).then((tid) => {
          if (tid && mountedRef.current) setLoading(false);
        });
      } else {
        supabase.auth.getSession().then(({ data: { session: s } }) => {
          if (s && mountedRef.current) checkSuperAdminLogin(s).then((tid) => {
            if (tid && mountedRef.current) setLoading(false);
          });
        });
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (mountedRef.current) {
        processSessionRef.current(newSession);
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: subscribe once, use refs for latest state/callbacks

  // ─── signOut ──────────────────────────────────────────────────────────────

  const signOut = useCallback(async () => {
    const uid = sessionRef.current?.user?.id;
    if (uid) {
      tenantProfileCache.delete(uid);
      try {
        localStorage.removeItem(`tenant_${uid}`);
        localStorage.removeItem('cached_tenant_id');
        localStorage.removeItem('cached_branch_id');
      } catch { /* ignore */ }
    }
    await supabase.auth.signOut();
    if (mountedRef.current) {
      setSession(null); setTenantId(null); setStoreSlug(null); setBranchId(null); setUserRole(null);
    }
  }, [supabase]);

  const isOwnerOrManager = useCallback(
    () => userRole === 'owner' || userRole === 'manager',
    [userRole],
  );

  // ─── switchTenant (Multi-tenant V1 — §18) ─────────────────────────────
  // SPA switch: invalida React Query, desconecta realtime, actualiza state.
  // V2 (>1k users): cambiar a hard navigation `window.location.href = /t/{slug}`.
  const switchTenant = useCallback(async (newTenantId: string) => {
    if (!newTenantId || newTenantId === tenantId) return;

    const uid = sessionRef.current?.user?.id;
    if (!uid) {
      logWarn('[AuthContext] switchTenant: sin sesión activa');
      return;
    }

    // Buscar en la lista cached. Si no está (caso típico: tenant recién
    // creado via /stores/new), invalidar cache + refetch antes de fallar.
    let target = tenants.find((t) => t.id === newTenantId);
    if (!target) {
      allTenantsCache.delete(uid);
      const fresh = await fetchAllTenantsCached(supabase, uid).catch(() => null);
      if (fresh) {
        setTenants(fresh);
        target = fresh.find((t) => t.id === newTenantId);
      }
    }
    if (!target) {
      logWarn('[AuthContext] switchTenant: tenant no encontrado ni en cache ni después de refetch', { newTenantId });
      throw new Error(`Tenant ${newTenantId} no existe o no tienes acceso.`);
    }

    // Invalidar todo el estado de React Query y realtime ANTES de cambiar el ID.
    // Acceso defensivo: si window.queryClient no existe (SSR/test), skip.
    try {
      const w = window as unknown as { queryClient?: { clear: () => void }; supabase?: SupabaseClient };
      w.queryClient?.clear();
      // Desconectar TODOS los canales realtime del cliente Supabase global.
      // Cada componente que use realtime se re-suscribirá con el nuevo tenant_id.
      await supabase.removeAllChannels();
    } catch (e) {
      logWarn('[AuthContext] switchTenant: error invalidando caches', e);
    }

    // Invalidar caches module-level del AuthContext que mapean userId → tenant.
    // Sin esto, el siguiente TOKEN_REFRESHED retornaría el tenant viejo y
    // pisaría el switch en background.
    tenantProfileCache.delete(uid);
    tenantProfileInFlight.delete(uid);

    // Actualizar estado del tenant activo
    setTenantId(newTenantId);
    setStoreSlug(target.slug);
    setBranchId(target.branchId);
    setUserRole(target.role);
    setCachedTenant(uid, newTenantId, target.branchId, target.role, target.slug);
    localStorage.setItem('cached_tenant_id', newTenantId);
    localStorage.setItem('lastActiveTenantId', newTenantId);

    // Telemetría (§18 Sprint 9). Si analytics no existe (V1 todavía), es no-op.
    try {
      const w = window as unknown as { analytics?: { track: (e: string, p: Record<string, unknown>) => void } };
      w.analytics?.track('tenant_switched', {
        from_tenant_id: tenantId,
        to_tenant_id: newTenantId,
        switching_method: 'dropdown',
      });
    } catch { /* ignore */ }
  }, [tenantId, tenants, supabase]);

  // ─── refreshTenants — fuerza refetch de la lista del switcher (§18) ─────
  // Necesario después de crear/eliminar tenant: el cache module-level no
  // se entera solo. Sin esto, el dropdown muestra data stale hasta reload.
  const refreshTenants = useCallback(async () => {
    const uid = sessionRef.current?.user?.id;
    if (!uid) return;
    allTenantsCache.delete(uid);
    allTenantsInFlight.delete(uid);
    const fresh = await fetchAllTenantsCached(supabase, uid).catch(() => null);
    if (fresh && mountedRef.current) setTenants(fresh);
  }, [supabase]);

  const activeTenant = useMemo(
    () => tenants.find((t) => t.id === tenantId) ?? null,
    [tenants, tenantId],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      tenantId,
      storeSlug,
      branchId,
      userRole,
      user: session?.user ?? null,
      loading,
      signOut,
      isOwnerOrManager,
      // Multi-tenant V1
      tenants,
      activeTenantId: tenantId,
      activeTenant,
      hasMultipleTenants: tenants.length > 1,
      switchTenant,
      refreshTenants,
      routingMode: ROUTING_MODE,
    }),
    [session, tenantId, storeSlug, branchId, userRole, loading, signOut, isOwnerOrManager, tenants, activeTenant, switchTenant, refreshTenants],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
